import { Node, mergeAttributes } from "@tiptap/core";
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type ReactNodeViewProps,
} from "@tiptap/react";
import { useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type ResizableImageAttrs = {
  src: string;
  alt?: string | null;
  title?: string | null;
  width?: number | null;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    image: {
      setImage: (options: ResizableImageAttrs) => ReturnType;
    };
  }
}

// ── Node View Component ──

function ResizableImageView({
  node,
  updateAttributes,
  selected,
}: ReactNodeViewProps) {
  const attrs = node.attrs as ResizableImageAttrs;
  const [resizing, setResizing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current =
        imgRef.current?.getBoundingClientRect().width ?? 300;

      const onMouseMove = (ev: MouseEvent) => {
        const diff = ev.clientX - startXRef.current;
        const newWidth = Math.max(50, Math.round(startWidthRef.current + diff));
        updateAttributes({ width: newWidth });
      };

      const onMouseUp = () => {
        setResizing(false);
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [updateAttributes],
  );

  return (
    <NodeViewWrapper className="relative inline-block my-2" data-drag-handle>
      <img
        ref={imgRef}
        src={attrs.src}
        alt={attrs.alt || ""}
        className={cn(
          "rounded-md block",
          selected && "ring-2 ring-primary",
          resizing && "pointer-events-none",
        )}
        style={{
          width: attrs.width ? `${attrs.width}px` : undefined,
          maxWidth: "100%",
          height: "auto",
        }}
        draggable={false}
      />
      {selected && (
        <div
          className="absolute top-0 right-0 w-3 h-full cursor-ew-resize flex items-center justify-center"
          onMouseDown={onMouseDown}
        >
          <div className="w-1 h-8 bg-primary rounded-full opacity-70 hover:opacity-100" />
        </div>
      )}
    </NodeViewWrapper>
  );
}

// ── TipTap Extension ──

export const ResizableImage = Node.create({
  name: "image",

  group: "block",

  atom: true,

  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      width: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: "img[src]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = { ...HTMLAttributes };
    if (attrs.width) {
      attrs.style = `width: ${attrs.width}px; max-width: 100%; height: auto;`;
      delete attrs.width;
    }
    return [
      "img",
      mergeAttributes({ class: "max-w-full h-auto rounded-md my-2" }, attrs),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },

  addCommands() {
    return {
      setImage:
        (options) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: options,
          }),
    };
  },
});
